// Redux
import { batch } from 'react-redux';
import { AsyncResult, Action, ApiAction, Result } from '../../index';
import api from '../../redux-api';

// Utils
import { removeManyHoldings } from '@/utils/portfolios';
import { getMonthEndDataOption } from '@/utils/comparison';

// Types
import { StockStoreState } from '../types/store-stock';
import Stock from '@/types/stock';
import PERIOD_END_DATA_OPTIONS from '@/types/period-end-data-options';

// TODO -- Merge with other stores (CON-289)

// Initial State
const INITIAL_STATE = {
  stocks: {},
  stocksPMED: {},
  stocksRMED: {},
  stocksPQED: {},
  stocksRQED: {},
};

// Actions
const FETCH_STOCK_BY_ID: ApiAction = {
  type: 'connect/stocks/store/fetch',
  endpoint: 'stocks/:id/',
};

const FETCH_MANY_STOCKS: ApiAction = {
  type: 'connect/stocks/store/fetch-many',
  endpoint: 'stocks/',
};

const STOCKS_STORE_GET = 'connect/stocks/store/get';
const STOCKS_STORE_GET_MANY = 'connect/stocks/store/get-many';
const STOCKS_STORE_ADD = 'connect/stocks/store/add';
const STOCKS_STORE_REMOVE_MANY = 'connect/stocks/store/remove-many';

// Reducer
export default function reducer(state: StockStoreState = INITIAL_STATE, action: Action): StockStoreState {
  switch (action.type) {
    case STOCKS_STORE_ADD:
      switch (action.monthEndDataOption) {
        case PERIOD_END_DATA_OPTIONS.RECENT_MONTH_END:
          return {
            ...state,
            stocksRMED: { ...state.stocksRMED, [action.stock._id]: action.stock },
          };
        case PERIOD_END_DATA_OPTIONS.PREVIOUS_MONTH_END:
          return {
            ...state,
            stocksPMED: { ...state.stocksPMED, [action.stock._id]: action.stock },
          };
        case PERIOD_END_DATA_OPTIONS.RECENT_QUARTER_END:
          return {
            ...state,
            stocksRQED: { ...state.stocksRQED, [action.stock._id]: action.stock },
          };
        case PERIOD_END_DATA_OPTIONS.PREVIOUS_QUARTER_END:
          return {
            ...state,
            stocksPQED: { ...state.stocksPQED, [action.stock._id]: action.stock },
          };
        default:
          return {
            ...state,
            stocks: { ...state.stocks, [action.stock._id]: action.stock },
          };
      }
    case STOCKS_STORE_REMOVE_MANY:
      return {
        ...state,
        stocksRMED: removeManyHoldings(state.stocksRMED, action.ids),
        stocksPMED: removeManyHoldings(state.stocksPMED, action.ids),
        stocksRQED: removeManyHoldings(state.stocksRQED, action.ids),
        stocksPQED: removeManyHoldings(state.stocksPQED, action.ids),
        stocks: removeManyHoldings(state.stocks, action.ids),
      };
    default:
      return state;
  }
}

// Action Creators
/**
 * Removes stock data from store
 * @param ids Array of stock's unique identifier
 */
export const invalidateStockData =
  (ids: string[]): Result<void> =>
  dispatch => {
    dispatch({ type: STOCKS_STORE_REMOVE_MANY, ids });
  };

/**
 * Retrieve Stock data either from memory, local cache or directly from the API
 * @param id Stock's unique identifier
 */
export const getStockData =
  (id: string, errorWhiteList?: string[]): AsyncResult<Stock> =>
  (dispatch, getState) => {
    dispatch({ type: STOCKS_STORE_GET, id });

    const monthEndDataOption = getMonthEndDataOption(getState().comparison);

    // See if we got it in State
    let stateStock;
    switch (monthEndDataOption) {
      case PERIOD_END_DATA_OPTIONS.RECENT_MONTH_END:
        stateStock = getState().stocks.store.stocksRMED[id];
        break;
      case PERIOD_END_DATA_OPTIONS.PREVIOUS_MONTH_END:
        stateStock = getState().stocks.store.stocksPMED[id];
        break;
      case PERIOD_END_DATA_OPTIONS.RECENT_QUARTER_END:
        stateStock = getState().stocks.store.stocksRQED[id];
        break;
      case PERIOD_END_DATA_OPTIONS.PREVIOUS_QUARTER_END:
        stateStock = getState().stocks.store.stocksPQED[id];
        break;
      default:
        stateStock = getState().stocks.store.stocks[id];
        break;
    }

    if (stateStock) {
      return Promise.resolve(stateStock);
    }

    return api
      .get(FETCH_STOCK_BY_ID, { id, ...(monthEndDataOption && { monthEndDataOption }) }, undefined, errorWhiteList)
      .then(({ stock }: { stock: Stock }) => {
        dispatch({ type: STOCKS_STORE_ADD, stock, monthEndDataOption });
        return stock;
      });
  };

/**
 * Retrieve multiple Stocks data either from memory, local cache or directly from the API
 * @param ids Stocks' unique identifiers
 */
export const getManyStocksData =
  (rawIds: string[]): AsyncResult<Stock[]> =>
  (dispatch, getState) => {
    const ids: string[] = rawIds.filter(id => id != null && id !== '');
    dispatch({ type: STOCKS_STORE_GET_MANY, ids });

    const monthEndDataOption = getMonthEndDataOption(getState().comparison);

    const remaining = new Set(ids);
    const stocksData = [] as Stock[];

    for (const id of ids) {
      if (id !== null) {
        // See if we got it in State
        let stateStock;
        switch (monthEndDataOption) {
          case PERIOD_END_DATA_OPTIONS.RECENT_MONTH_END:
            stateStock = getState().stocks.store.stocksRMED[id];
            break;
          case PERIOD_END_DATA_OPTIONS.PREVIOUS_MONTH_END:
            stateStock = getState().stocks.store.stocksPMED[id];
            break;
          case PERIOD_END_DATA_OPTIONS.RECENT_QUARTER_END:
            stateStock = getState().stocks.store.stocksRQED[id];
            break;
          case PERIOD_END_DATA_OPTIONS.PREVIOUS_QUARTER_END:
            stateStock = getState().stocks.store.stocksPQED[id];
            break;
          default:
            stateStock = getState().stocks.store.stocks[id];
            break;
        }
        if (stateStock) {
          stocksData.push(stateStock);
          remaining.delete(id);
          continue;
        }
      } else {
        stocksData.push();
        remaining.delete(id);
        continue;
      }
    }
    // Fetch the rest from the API if we're missing any
    if (remaining.size) {
      return api
        .get(FETCH_MANY_STOCKS, { ids: Array.from(remaining.values()).join('-'), ...(monthEndDataOption && { monthEndDataOption }) })
        .then(({ stocks }: { stocks: Stock[] }) => {
          batch(() => {
            for (const stock of stocks) {
              dispatch({ type: STOCKS_STORE_ADD, stock, monthEndDataOption });
            }
          });

          return stocksData.concat(stocks);
        });
    }

    // Return the cached stocks if no API call is required
    return Promise.resolve(stocksData);
  };
